import { WA_WINDOW } from '@ng-web-apis/common';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AccountService } from '../account/account.service';
import { LocalStorageService } from '@core/services/local-storage.service';
import { TUI_THEME_DEFAULT_KEY } from '@core/constants';

export type ThemeMode = 'light' | 'dark' | 'system';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly window = inject<Window>(WA_WINDOW);
  private readonly storage = inject(LocalStorageService);
  private readonly key = TUI_THEME_DEFAULT_KEY;
  private readonly accountService = inject(AccountService);

  private readonly themeSubject: BehaviorSubject<ThemeMode>;
  readonly theme$: Observable<ThemeMode>;

  constructor() {
    const stored: 'dark' | 'light' | 'system' | null = this.storage.getItem(
      this.key,
    );
    let initial: ThemeMode = 'system';

    if (stored) {
      initial = stored;
    }

    this.themeSubject = new BehaviorSubject<ThemeMode>(initial);
    this.theme$ = this.themeSubject.asObservable();

    // Listen to system theme changes if in system mode
    const mediaDark = this.window.matchMedia('(prefers-color-scheme: dark)');

    mediaDark.addEventListener('change', () => {
      if (this.getTheme() === 'system') {
        this.themeSubject.next('system'); // triggers subscribers
      }
    });

    this.accountService.themeChanged$.subscribe(theme => {
      this.themeSubject.next(theme);
    });
  }

  setTheme(theme: ThemeMode) {
    this.themeSubject.next(theme);
    this.storage.setItem(this.key, theme);
    this.accountService
      .createOrUpdateUserSettings$({ common: { theme } })
      .subscribe();
  }

  getTheme(): ThemeMode {
    return this.themeSubject.getValue();
  }

  /**
   * Returns the effective theme: 'dark' | 'light', based on user selection and system preference
   */
  getEffectiveTheme(): 'dark' | 'light' {
    const theme = this.getTheme();

    if (theme === 'system') {
      return this.window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
    }

    return theme;
  }

  /**
   * Returns the stored theme value from localStorage, or null if not set
   */
  getStored(): ThemeMode {
    return this.storage.getItem<ThemeMode>(this.key) || 'system';
  }
}
